import type { Definition, RecipeView, SettingView } from '../api/missions';

/**
 * A recipe's own settings (release D, D6.1/D6.3; ENGINE_DESIGN.md §2.9, §2.19
 * as amended): request switches — optional twists the owner made switches,
 * off by default and never rolled — and multi-value choices. The wizard's last
 * step and the designer's *Guió* panel read them through these.
 */

/** The switches, shown under «Girs». */
export function switchesOf(recipe: RecipeView | null | undefined): SettingView[] {
  return (recipe?.settings ?? []).filter((s) => s.kind === 'switch');
}

/** The multi-value choices, shown in «Opcions d'aquest tipus de missió». */
export function choicesOf(recipe: RecipeView | null | undefined): SettingView[] {
  return (recipe?.settings ?? []).filter((s) => s.kind === 'choice');
}

/** A setting's value as chosen, else its own default. */
export function settingValue(setting: SettingView, chosen: Record<string, string>): string {
  const v = chosen[setting.id];
  return v && setting.values.includes(v) ? v : setting.default;
}

/**
 * What the request carries: only the settings this recipe declares, with a
 * value it admits, and only those set away from the default — null when none,
 * which the generator reads as every default.
 */
export function settingsToSend(
  recipe: RecipeView | null | undefined,
  chosen: Record<string, string>,
): Record<string, string> | null {
  const out: Record<string, string> = {};
  for (const s of recipe?.settings ?? []) {
    const v = chosen[s.id];
    if (v && s.values.includes(v) && v !== s.default) out[s.id] = v;
  }
  return Object.keys(out).length ? out : null;
}

/** A setting's value as the generated mission resolved it (`script.settings`), else its default. */
export function settingInDefinition(setting: SettingView, def: Definition): string {
  const v = def.script?.settings?.[setting.id]?.value;
  return v && setting.values.includes(v) ? v : setting.default;
}
